import { motion } from "framer-motion";
import { MessageCircle, MoveRight } from "lucide-react";
import { SaturnCanvas } from "@/components/SaturnCanvas";

const lineVariant = {
  hidden: { y: "115%", rotate: 2.5 },
  show: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { delay: 0.35 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = ({ company }) => {
  const waLink = `https://wa.me/${company?.wa_number || "6285714757850"}?text=${encodeURIComponent("Halo, saya ingin bertanya tentang layanan Planet Gudang")}`;

  return (
    <section id="beranda" data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      <img
        src="/assets/hero_bg_ai.png"
        alt=""
        aria-hidden="true"
        data-testid="hero-bg-photo"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-[#07090e]/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#07090e]/80 via-[#07090e]/35 to-[#07090e]/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07090e] via-transparent to-[#07090e]/50" />
      <div className="pointer-events-none absolute inset-0">
        <SaturnCanvas />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5, duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        className="pointer-events-none absolute -right-16 top-24 z-[5] w-[70vw] max-w-[520px] sm:-right-10 sm:top-14 sm:w-[50vw] lg:-right-6 lg:top-6 lg:w-[40vw] lg:max-w-[640px]"
      >
        <motion.img
          src="/assets/planet_ai.png"
          alt=""
          aria-hidden="true"
          data-testid="hero-planet-img"
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          className="w-full mix-blend-screen"
          style={{
            WebkitMaskImage: "radial-gradient(ellipse 72% 72% at 50% 50%, black 42%, transparent 72%)",
            maskImage: "radial-gradient(ellipse 72% 72% at 50% 50%, black 42%, transparent 72%)",
          }}
        />
      </motion.div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 pt-32 pb-20 sm:px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          data-testid="hero-eyebrow"
          className="font-mono-tech mb-8 text-xs uppercase tracking-[0.35em] text-amber-400"
        >
          {company?.name || "PT PLANET GUDANG ANTAR GALAKSI"} — Bekasi, Indonesia
        </motion.p>

        <h1 className="font-display text-[16vw] font-black uppercase leading-[0.88] tracking-tight sm:text-[12vw] lg:text-[8.5rem]" data-testid="hero-headline">
          <span className="mask-line">
            <motion.span custom={0} variants={lineVariant} initial="hidden" animate="show" className="text-white">
              Planet
            </motion.span>
          </span>
          <span className="mask-line">
            <motion.span custom={1} variants={lineVariant} initial="hidden" animate="show" className="text-amber-400">
              Gudang
            </motion.span>
          </span>
          <span className="mt-3 flex items-center gap-4">
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.95, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="h-[3px] w-10 shrink-0 origin-left bg-amber-400 sm:w-14"
            />
            <span className="mask-line">
              <motion.span custom={2} variants={lineVariant} initial="hidden" animate="show" className="text-2xl tracking-[0.18em] text-amber-400 sm:text-4xl lg:text-5xl">
                Antar Galaksi
              </motion.span>
            </span>
          </span>
        </h1>

        <div className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            data-testid="hero-tagline"
            className="max-w-md text-base leading-relaxed text-slate-400"
          >
            {company?.tagline || "Pusat Solusi Gudang, Konstruksi, Material & Alat Berat Antar Galaksi"}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="flex w-full max-w-md flex-col gap-4"
          >
            <a
              href="#katalog"
              data-testid="hero-cta-catalog"
              className="font-mono-tech group flex w-full items-center justify-center gap-3 rounded-xl bg-amber-400 px-8 py-5 text-xs font-bold uppercase tracking-[0.25em] text-[#07090e] transition-all duration-300 hover:-translate-y-1 hover:bg-amber-300 hover:shadow-[0_16px_40px_-12px_rgba(245,158,11,0.6)]"
            >
              Jelajahi Katalog
              <MoveRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="font-mono-tech group flex w-full items-center justify-center gap-3 rounded-xl border border-white/70 bg-transparent px-8 py-5 text-xs uppercase tracking-[0.25em] text-white transition-all duration-300 hover:-translate-y-1 hover:border-white hover:bg-white/10"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp Kami
            </a>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        data-testid="hero-scroll-indicator"
        className="absolute inset-x-0 bottom-8 z-10 flex flex-col items-center gap-3"
      >
        <span className="relative h-12 w-px overflow-hidden bg-white/15">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-x-0 h-1/2 bg-amber-400"
          />
        </span>
        <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-slate-400">Scroll</span>
      </motion.div>
    </section>
  );
};
